"use client";

import { QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { handleAuthStateChange } from "@/lib/cache/user-cache-utils";
import { createQueryClient } from "./query-provider";
import { useSupabase } from "./supabase-auth-provider";

interface QueryProviderWithAuthProps {
	children: ReactNode;
}

/**
 * Query Provider that listens to auth state changes
 * Must be used inside SupabaseProvider to access auth context
 */
export function QueryProviderWithAuth({
	children,
}: QueryProviderWithAuthProps) {
	const [queryClient] = useState(() => createQueryClient());
	const { supabase, user } = useSupabase();

	// Listen for auth state changes and clear user-specific caches
	useEffect(() => {
		const {
			data: { subscription },
		} = supabase.auth.onAuthStateChange((event) => {
			switch (event) {
				case "SIGNED_IN":
					handleAuthStateChange(queryClient, "SIGNED_IN");
					break;
				case "SIGNED_OUT":
					handleAuthStateChange(queryClient, "SIGNED_OUT");
					break;
				case "USER_UPDATED":
					if (user?.id) {
						handleAuthStateChange(queryClient, "USER_UPDATED", user.id);
					}
					break;
			}
		});

		return () => {
			subscription.unsubscribe();
		};
	}, [queryClient, supabase.auth, user?.id]);

	return (
		<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
	);
}
